import React from 'react'
import { Button, Col, Container, Form, FormGroup, Row } from 'reactstrap'
import Joi from 'joi-browser'
import TreeList from './common/treeList'
import http from '../services/httpService'
import { toast } from 'react-toastify'
import BaseForm from './common/baseForm'
import { NeighbourhoodsMultiSelect } from './common/neighbourhoods-multi-select'
import { Map } from './common/map'
import { HoveredOptionProvider } from '../contexts/hovered-option'
import AddressAutocomplete from './common/addressAutocomplete';

class SearchProfileForm extends BaseForm {
  state = {
    data: {
      id: null,
      locationSelectorList: [],
      name: null,
      userId: '',
      minPrice: 700,
      maxPrice: 1000,
      minPriceList: [],
      maxPriceList: [],
      sizeRangeList: [],
      bedroomsRangeList: [],
      furnishingList: [],
      cityList: [],
      radius: 0,
      radiusList: [],
      searchZipCodeIds: [],
      flexiblePrice: false,
      flexibleSizeRange: false,
      flexibleRoomRange: false,
      flexibleFurnishing: false,
      includePaidSources: false,
      allowedToShare: false,
      flexibleAllowedToShare: false,
      student: false,
      excludeSeniorHousing: false,
      excludeWoningNet: false,
      includeSocialHousing: false,
      excludeRooms: false,
      netSalary: '',
      grossSalary: '',
      overrideLatitude: '',
      overrideLongitude: '',
      radiusInMeters: 0,
      locationIds: [],
      excludeShortTermRentals: false,
      requireGarden: false,
      requireOutside: false,
      referenceAddress: null,
      referenceLatitude: null,
      referenceLongitude: null,
      transportMode: null,
      travelTimeMinutes: null,
      locationSelector: ''
    },
    tree: [],
    neighbourhoods: [],
    cityCoordinates: {
      lat: 0,
      lng: 0,
    },
    travelDistancePolygon: null,
    errors: {}
  }

  schema = {
    id: Joi.optional(),
    name: Joi.optional(),
    isActive: Joi.boolean(),
    flexiblePrice: Joi.boolean(),
    flexibleSharingNotAllowed: Joi.boolean(),
    flexibleSizeRange: Joi.boolean(),
    flexibleRoomRange: Joi.boolean(),
    flexibleFurnishing: Joi.boolean(),
    flexibleZipCodes: Joi.any(),
    includePaidSources: Joi.boolean(),
    netSalary: Joi.optional(),
    grossSalary: Joi.optional(),
    allowedToShare: Joi.boolean(),
    flexibleAllowedToShare: Joi.boolean(),
    student: Joi.boolean(),
    excludeSeniorHousing: Joi.boolean(),
    excludeWoningNet: Joi.boolean(),
    includeSocialHousing: Joi.boolean(),
    excludeRooms: Joi.boolean(),
    requireGarden: Joi.boolean(),
    requireOutside: Joi.boolean(),
    radius: Joi.number().when('locationSelector', {
      is: 'Radius',
      then: Joi.number().positive().required(),
      otherwise: Joi.number(),
    }),
    radiusInMeters: Joi.number().optional(),
    radiusList: Joi.array(),
    locationSelector: Joi.string(),
    locationSelectorList: Joi.array(),
    minPrice: Joi.number().required(),
    maxPrice: Joi.number().required(),
    sizeRangeId: Joi.string().required(),
    cityId: Joi.string().required(),
    roomRangeId: Joi.string().required(),
    furnishingId: Joi.string().required(),
    minPriceList: Joi.array(),
    maxPriceList: Joi.array(),
    sizeRangeList: Joi.array(),
    cityList: Joi.array(),
    bedroomsRangeList: Joi.array(),
    furnishingList: Joi.array(),
    searchZipCodeIds: Joi.array()
      .label('Neighbourhoods (by postcodes)')
      .when('locationSelector', {
        is: 'Neighbourhood',
        then: Joi.array().min(1).required(),
        otherwise: Joi.array(),
      }),
    zipCodesTree: Joi.array(),
    createDate: Joi.string(),
    modifiedDate: Joi.string(),
    overrideLatitude: Joi.alternatives().try(Joi.number().min(-90).max(90), Joi.string().allow('')),
    overrideLongitude: Joi.alternatives().try(Joi.number().min(-180).max(180), Joi.string().allow('')),
    locationIds: Joi.array()
      .label('Neighbourhoods (by geolocation)')
      .when('locationSelector', {
        is: 'Geolocation',
        then: Joi.array().min(1).required(),
        otherwise: Joi.array(),
      }),
    locations: Joi.array(),
    userId: Joi.string(),
    excludeShortTermRentals: Joi.boolean(),
    referenceAddress: Joi.when('locationSelector', {
      is: 'TravelDistance',
      then: Joi.string().required(),
      otherwise: Joi.alternatives().try(Joi.string().allow(''), Joi.allow(null)).optional(),
    }),
    referenceLatitude: Joi.alternatives().try(
      Joi.number().min(-90).max(90),
      Joi.string().allow(''),
      Joi.allow(null)
    ).optional(),
    referenceLongitude: Joi.alternatives().try(
      Joi.number().min(-180).max(180),
      Joi.string().allow(''),
      Joi.allow(null)
    ).optional(),
    transportMode: Joi.when('locationSelector', {
      is: 'TravelDistance',
      then: Joi.string().valid('walk', 'bicycle', 'drive', 'approximated_transit').required(),
      otherwise: Joi.alternatives().try(Joi.string().allow(''), Joi.allow(null)).optional(),
    }),
    travelTimeMinutes: Joi.when('locationSelector', {
      is: 'TravelDistance',
      then: Joi.alternatives().try(
        Joi.number().valid(15, 30, 45, 60),
        Joi.string().allow('')
      ).required(),
      otherwise: Joi.alternatives().try(
        Joi.number(),
        Joi.string().allow(''),
        Joi.allow(null)
      ).optional(),
    })
  }

  async componentDidMount () {
    const searchProfileId = this.props.match.params.id

    const { data } = this.state
    data.userId = this.props.match.params.userId
    var urlTemplate = `search-profiles`
    if (searchProfileId !== 'new') {
      urlTemplate += `/${searchProfileId}`
    }

    const { data: searchProfile } = await http.get(urlTemplate)

    this.loadNeighbourhoods(searchProfile.cityId).then((neighbourhoods) => {
      this.setState({ neighbourhoods });
    });

    this.loadCityCoordinates(searchProfile.cityId).then((cityCoordinates) => {
      this.setState({ cityCoordinates });
    });

    const radiusInMeters = searchProfile.radius > 50 ? searchProfile.radius : 0

    const tree = JSON.parse(JSON.stringify(searchProfile.zipCodesTree))

    // Ensure default values for travel distance fields when location selector is TravelDistance
    const dataWithDefaults = { ...searchProfile, radiusInMeters };

    // Ensure number fields are never null to prevent controlled/uncontrolled warnings
    const numberFields = ['netSalary', 'grossSalary', 'overrideLatitude', 'overrideLongitude', 'referenceLatitude', 'referenceLongitude'];
    numberFields.forEach(field => {
      if (dataWithDefaults[field] === null || dataWithDefaults[field] === undefined) {
        dataWithDefaults[field] = '';
      }
    });

    // Filter out null/undefined values from locationIds to prevent sparse array validation errors
    if (dataWithDefaults.locationIds && Array.isArray(dataWithDefaults.locationIds)) {
      dataWithDefaults.locationIds = dataWithDefaults.locationIds.filter(id => id != null);
    }

    if (dataWithDefaults.locationSelector === 'TravelDistance') {
      if (!dataWithDefaults.transportMode) {
        dataWithDefaults.transportMode = 'walk';
      }
      if (!dataWithDefaults.travelTimeMinutes) {
        dataWithDefaults.travelTimeMinutes = 15;
      } else if (typeof dataWithDefaults.travelTimeMinutes === 'string') {
        // Convert string format "45 minutes" to numeric value 45
        const numericValue = parseInt(dataWithDefaults.travelTimeMinutes.toString().replace(' minutes', ''));
        if (!isNaN(numericValue)) {
          dataWithDefaults.travelTimeMinutes = numericValue;
        } else {
          dataWithDefaults.travelTimeMinutes = 15;
        }
      }
    }

    this.setState({ data: dataWithDefaults, tree })

    // Load travel distance polygon if all required fields are present
    if (dataWithDefaults.locationSelector === 'TravelDistance' &&
        dataWithDefaults.id &&
        dataWithDefaults.referenceAddress &&
        dataWithDefaults.referenceLatitude &&
        dataWithDefaults.referenceLongitude &&
        dataWithDefaults.transportMode &&
        dataWithDefaults.travelTimeMinutes) {
      this.loadTravelDistancePolygon(dataWithDefaults);
    }
  }

  loadNeighbourhoods = async (cityId) => {
    return await http
      .get(`/cities/${cityId}/neighbourhoods`)
      .then((response) =>
        response.data.map((item) => ({
          ...item,
          type: 'Feature',
        }))
      )
      .catch((error) => {
        console.error(error);
        return [];
      });
  };

  loadCityCoordinates = async (cityId) => {
    return await http
      .get(`/cities/${cityId}/details`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        return {
          lat: 0,
          lng: 0,
        };
      });
  }

  loadTravelDistancePolygon = async (searchProfileData) => {
    try {
      const { id, referenceAddress, referenceLatitude, referenceLongitude, transportMode, travelTimeMinutes, locationSelector } = searchProfileData;

      // Convert travel time from "15 minutes" format to just the number
      const travelTimeNumber = parseInt(travelTimeMinutes.toString().replace(' minutes', ''));

      const response = await http.post(`SearchProfiles/travel-distance-polygon`, {
        searchProfileId: id,
        locationSelector: locationSelector,
        referenceAddress,
        latitude: referenceLatitude,
        longitude: referenceLongitude,
        transportMode: transportMode,
        travelTimeMinutes: travelTimeNumber
      });

      if (response.data.success && response.data.polygon) {
        this.setState({ travelDistancePolygon: response.data.polygon });
      }
    } catch (error) {
      console.error('Error loading travel distance polygon:', error);
    }
  }

  doSubmit = async () => {
    try {
      if (this.state.data.radiusInMeters > 50) {
        this.state.data.radius = this.state.data.radiusInMeters
      }

      // Prepare data for submission by converting empty strings to null for number fields
      const dataForSubmission = { ...this.state.data };
      const numberFields = ['netSalary', 'grossSalary', 'overrideLatitude', 'overrideLongitude', 'referenceLatitude', 'referenceLongitude'];
      numberFields.forEach(field => {
        if (dataForSubmission[field] === '') {
          dataForSubmission[field] = null;
        }
      });

      const searchProfileId = this.props.match.params.id
      if (searchProfileId !== 'new') {
        await http.put(
          'search-profiles/' + searchProfileId,
          dataForSubmission
        )
      } else {
        await http.post(
          'search-profiles',
          { ...dataForSubmission, userId: this.props.match.params.userId }
        )
      }
      toast.success('search profile saved')
      this.props.history.goBack()
    } catch (ex) {
      if (ex.response && ex.response.status === 400) {
        const errors = { ...this.state.errors }
        errors.username = ex.response.data
        this.setState({ errors })
      }
    }
  }

  handleCityChange = async ({ currentTarget: input }) => {
    this.setState((previous) => ({
      ...previous,
      cityCoordinates: {
        lat: 0,
        lng: 0,
      },
      data: {
        ...previous.data,
        cityId: input.value,
        searchZipCodeIds: [],
        locationIds: [],
        overrideLatitude: 0,
        overrideLongitude: 0,
      },
    }));

    const { data: tree } = await http.get(
      'searchprofiles/zipcodes/' + input.value
    );

    this.setState({ tree });

    this.loadNeighbourhoods(input.value).then((neighbourhoods) => {
      this.setState({ neighbourhoods });
    });

    this.loadCityCoordinates(input.value).then((cityCoordinates) => {
      this.setState({ cityCoordinates });
    });
  }


  handleTreeChange = (searchZipCodeIds) => {
    const { data } = this.state
    data.searchZipCodeIds = searchZipCodeIds
    this.setState({ data })
  }

  handleDelete = async e => {
    e.preventDefault()

    if (window.confirm('do you want to delete this search profile?')) {
      await http.delete('searchprofiles/' + this.state.data.id)

      toast.success('search profile deleted')

      this.props.history.goBack()
    }
  }

  handleRadiusInMetersChange = ({ currentTarget: input }) => {
    const { data } = this.state
    data.radiusInMeters = input.value ? parseInt(input.value, 10) : null
    this.setState({ data })
  }

  handleRadiusChange = ({ currentTarget }) => {
    this.handleChange({ currentTarget });
    this.setState((previous) => ({
      data: {
        ...previous.data,
        radiusInMeters: 0,
      },
    }));
  };

  handleNeighbourhoodsChange = (nextLocationIds) => {
    this.setState({
      data: {
        ...this.state.data,
        locationIds: nextLocationIds,
      },
    });
  };

  handleMarkerChange = (marker) => {
    this.setState((previous) => ({
      data: {
        ...previous.data,
        overrideLatitude: marker.lat,
        overrideLongitude: marker.lng,
      },
    }));
  };

  handleLocationSelectorChange = ({ currentTarget: input }) => {
    this.setState((previous) => ({
      data: {
        ...previous.data,
        locationSelector: input.value,
        // Clear fields when switching selector types
        searchZipCodeIds: [],
        locationIds: [],
        overrideLatitude: 0,
        overrideLongitude: 0,
        radius: 0,
        radiusInMeters: 0,
        referenceAddress: null,
        referenceLatitude: null,
        referenceLongitude: null,
        transportMode: null,
        travelTimeMinutes: null,
      },
    }));
  };

  handleAddressSelect = (addressData) => {
    this.setState((previous) => ({
      data: {
        ...previous.data,
        referenceAddress: addressData.address,
        referenceLatitude: addressData.latitude,
        referenceLongitude: addressData.longitude,
      },
    }), () => {
      // Check if all travel distance fields are filled after address selection
      const { referenceAddress, referenceLatitude, referenceLongitude, transportMode, travelTimeMinutes, id } = this.state.data;

      if (id && referenceAddress && referenceLatitude && referenceLongitude && transportMode && travelTimeMinutes) {
        this.handleUpdateTravelDistancePolygon();
      }
    });
  };

  handleUpdateTravelDistancePolygon = async () => {
    try {
      const { id, referenceAddress, referenceLatitude, referenceLongitude, transportMode, travelTimeMinutes, locationSelector } = this.state.data;

      if (!id || !referenceAddress || !referenceLatitude || !referenceLongitude || !transportMode || !travelTimeMinutes) {
        toast.error('Please fill in all travel distance fields before updating polygon');
        return;
      }

      // Convert travel time from "15 minutes" format to just the number
      const travelTimeNumber = parseInt(travelTimeMinutes.toString().replace(' minutes', ''));

      const response = await http.post(`SearchProfiles/travel-distance-polygon`, {
        searchProfileId: id,
        locationSelector: locationSelector,
        referenceAddress,
        latitude: referenceLatitude,
        longitude: referenceLongitude,
        transportMode: transportMode,
        travelTimeMinutes: travelTimeNumber
      });

      if (response.data.success) {
        toast.success('Travel distance polygon updated successfully');

        // Store the polygon data in state for map visualization
        if (response.data.polygon) {
          this.setState({ travelDistancePolygon: response.data.polygon });
        }
      }
    } catch (ex) {
      console.error('Error updating travel distance polygon:', ex);
      toast.error('Failed to update travel distance polygon: ' + (ex.response?.data?.error || ex.message));
    }
  };

  handleTravelDistanceFieldChange = (field, value) => {
    this.handleChange({ currentTarget: { name: field, value } });

    // Check if all travel distance fields are filled after this change
    setTimeout(() => {
      const { referenceAddress, referenceLatitude, referenceLongitude, transportMode, travelTimeMinutes, id } = this.state.data;

      if (id && referenceAddress && referenceLatitude && referenceLongitude && transportMode && travelTimeMinutes) {
        this.handleUpdateTravelDistancePolygon();
      }
    }, 100);
  };

  render() {
    const {
      id,
      minPriceList,
      maxPriceList,
      sizeRangeList,
      bedroomsRangeList,
      furnishingList,
      cityList,
      searchZipCodeIds,
      radiusList,
      locationSelectorList,
      locationSelector,
    } = this.state.data;

    const errors = this.validate();

    const isOverrideCoordinatesValid =
      this.state.data.overrideLatitude &&
      this.state.data.overrideLongitude &&
      !errors?.['overrideLatitude'] &&
      !errors?.['overrideLongitude'];

    const markerCoordinates = {
      lat: isOverrideCoordinatesValid
        ? this.state.data.overrideLatitude
        : this.state.cityCoordinates.lat,
      lng: isOverrideCoordinatesValid
        ? this.state.data.overrideLongitude
        : this.state.cityCoordinates.lng,
    };

    return (
      <Container>
        <Form onSubmit={this.handleSubmit}>
          <Row className="mt-4">
            <Col>
              <Row>
                <Col sm="8">
                  <FormGroup row>
                    <Col>{this.renderInput('name', 'Name')}</Col>
                    <Col>
                      {this.renderSelect('minPrice', 'Min price', minPriceList)}
                    </Col>
                    <Col>
                      {this.renderSelect('maxPrice', 'Max price', maxPriceList)}
                    </Col>
                  </FormGroup>

                  <FormGroup row className="my-3">
                    <Col>
                      {this.renderSelect('sizeRangeId', 'Size', sizeRangeList)}
                    </Col>
                    <Col>
                      {this.renderSelect('roomRangeId', 'Room range', bedroomsRangeList)}
                    </Col>

                  </FormGroup>
                  <FormGroup row className="my-3">
                    <Col>
                      {this.renderSelect('furnishingId', 'Furnishing', furnishingList)}
                    </Col>
                    <Col>
                      {this.renderSelect('cityId', 'City', cityList, this.handleCityChange)}
                    </Col>
                  </FormGroup>
                </Col>
                <Col>
                <FormGroup check>
                    <Col>
                      {this.renderCheckbox(
                        'flexibleFurnishing',
                        'Flexible furnishing'
                      )}
                    </Col>
                    <Col>
                      {this.renderCheckbox(
                        'includePaidSources',
                        'Include PaidSources'
                      )}
                    </Col>
                    <Col>
                      {this.renderCheckbox(
                        'allowedToShare',
                        'Allowed to share'
                      )}
                    </Col>
                    <Col>
                      {this.renderCheckbox(
                        'flexibleAllowedToShare',
                        'Flexible allowed to share'
                      )}
                    </Col>
                    <Col>{this.renderCheckbox('student', 'Student')}</Col>
                    <Col>
                      {this.renderCheckbox(
                        'excludeSeniorHousing',
                        'Exclude 50+'
                      )}
                    </Col>
                    <Col>
                      {this.renderCheckbox(
                        'excludeWoningNet',
                        'Exclude WoningNet'
                      )}
                    </Col>
                    <Col>
                      {this.renderCheckbox(
                        'includeSocialHousing',
                        'Include Social Housing'
                      )}
                    </Col>
                    <Col>
                      {this.renderCheckbox('excludeRooms', 'Exclude Rooms')}
                    </Col>
                    <Col>
                      {this.renderCheckbox('excludeShortTermRentals', 'Exclude Short Term Rentals')}
                    </Col>
                    <Col>
                      {this.renderCheckbox('requireOutside', 'Require Outside Space')}
                    </Col>
                    <Col>
                      {this.renderCheckbox('requireGarden', 'Require Garden')}
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col>
            <FormGroup row>
                <HoveredOptionProvider>
                  <Col md="6">
                    {this.renderSelect(
                      'locationSelector',
                      'Location selector (defines which method will be used for matching)',
                      locationSelectorList,
                      this.handleLocationSelectorChange
                    )}
                    {locationSelector === 'Geolocation' && (
                      <NeighbourhoodsMultiSelect
                        values={this.state.data.locationIds}
                        onChange={this.handleNeighbourhoodsChange}
                        neighbourhoodsData={this.state.neighbourhoods}
                      />
                    )}
                    {locationSelector === 'Neighbourhood' && (
                      <>
                        {this.state.errors['searchZipCodeIds'] && (
                          <div className="alert alert-danger">
                            {this.state.errors['searchZipCodeIds']}
                          </div>
                        )}
                        {searchZipCodeIds && (
                          <div>
                            <label>Neighbourhoods (by postcodes)</label>
                            <TreeList
                              zipCodesTree={this.state.tree}
                              onTreeChange={this.handleTreeChange}
                              searchZipCodeIds={searchZipCodeIds}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {locationSelector === 'Radius' && (
                      <>
                        {this.renderSelect(
                          'radius',
                          'Radius',
                          radiusList,
                          this.handleRadiusChange
                        )}
                        {this.renderInput(
                          'radiusInMeters',
                          'Radius in meters (overrides dropdown value)',
                          'number'
                        )}
                        <FormGroup row>
                          <Col>
                            {this.renderInput(
                              'overrideLatitude',
                              'override Latitude (radius center)',
                              'number'
                            )}
                            {this.state.data.overrideLatitude !== 0.0 &&
                              this.state.data.overrideLongitude !== 0.0 && (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${this.state.data.overrideLatitude},${this.state.data.overrideLongitude}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Show override coordinates on map
                                </a>
                              )}
                          </Col>
                          <Col>
                            {this.renderInput(
                              'overrideLongitude',
                              'override Longitude (radius center)',
                              'number'
                            )}
                          </Col>
                        </FormGroup>
                      </>
                    )}
                    {locationSelector === 'TravelDistance' && (
                      <>
                        <FormGroup>
                          <label htmlFor="referenceAddress">Reference Address</label>
                          <AddressAutocomplete
                            name="referenceAddress"
                            value={this.state.data.referenceAddress}
                            onChange={this.handleChange}
                            onAddressSelect={this.handleAddressSelect}
                            placeholder="Enter reference address (e.g., Amsterdam Central Station)"
                            className="form-control"
                          />
                          {this.state.errors['referenceAddress'] && (
                            <div className="alert alert-danger">
                              {this.state.errors['referenceAddress']}
                            </div>
                          )}
                        </FormGroup>
                        <FormGroup row>
                          <Col>
                            {this.renderSelect(
                              'transportMode',
                              'Transport Mode',
                              [
                                { value: 'walk', name: 'Walking' },
                                { value: 'bicycle', name: 'Cycling' },
                                { value: 'drive', name: 'Driving' },
                                { value: 'approximated_transit', name: 'Public Transport' }
                              ],
                              (e) => this.handleTravelDistanceFieldChange('transportMode', e.currentTarget.value)
                            )}
                          </Col>
                          <Col>
                            {this.renderSelect(
                              'travelTimeMinutes',
                              'Travel Time',
                              [
                                { value: 15, name: '15 minutes' },
                                { value: 30, name: '30 minutes' },
                                { value: 45, name: '45 minutes' },
                                { value: 60, name: '60 minutes' }
                              ],
                              (e) => this.handleTravelDistanceFieldChange('travelTimeMinutes', e.currentTarget.value)
                            )}
                          </Col>
                        </FormGroup>
                        <FormGroup row>
                          <Col>
                            {this.renderInput(
                              'referenceLatitude',
                              'Reference Latitude',
                              'number'
                            )}
                          </Col>
                          <Col>
                            {this.renderInput(
                              'referenceLongitude',
                              'Reference Longitude',
                              'number'
                            )}
                          </Col>
                        </FormGroup>
                        {this.state.data.referenceLatitude &&
                         this.state.data.referenceLongitude && (
                          <p>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${this.state.data.referenceLatitude},${this.state.data.referenceLongitude}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Show reference location on map
                            </a>
                          </p>
                        )}
                        {id && (
                          <Button
                            onClick={this.handleUpdateTravelDistancePolygon}
                            color="primary"
                            className="mt-2"
                          >
                            Update Travel Distance Polygon
                          </Button>
                        )}
                      </>
                    )}
                  </Col>
                  <Col md="6">
                    <div style={{  height: '400px' }}>
                      <Map
                        locationSelector={this.state.data.locationSelector}
                        neighbourhoodsData={this.state.neighbourhoods}
                        locationIds={this.state.data.locationIds}
                        onMarkerChange={this.handleMarkerChange}
                        radiusKm={
                          this.state.data.radiusInMeters
                            ? this.state.data.radiusInMeters / 1000
                            : this.state.data.radius
                        }
                        cityCoordinates={this.state.cityCoordinates}
                        marker={markerCoordinates}
                        travelDistancePolygon={this.state.travelDistancePolygon}
                        referenceAddress={
                          this.state.data.referenceLatitude && this.state.data.referenceLongitude
                            ? {
                                lat: this.state.data.referenceLatitude,
                                lng: this.state.data.referenceLongitude
                              }
                            : null
                        }
                      />
                    </div>
                  </Col>
                </HoveredOptionProvider>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col>{this.renderInput('netSalary', 'Net salary', 'number')}</Col>
            <Col>{this.renderInput('grossSalary', 'Gross salary', 'number')}</Col>
          </Row>
          <Row>
            {errors && (
              <ul>
                {Object.values(errors).map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}
          </Row>
          <Row>
            <Col sm="1">{this.renderButton('Save')}</Col>
            <Col sm="1">
              <Button onClick={this.props.history.goBack}> Back </Button>
            </Col>
            {id && (
              <Col>
                <Button onClick={this.handleDelete} color="danger"> Delete </Button>
              </Col>
            )}
          </Row>
        </Form>
      </Container>
    )
  }
}

export default SearchProfileForm
